import React, { Fragment, useMemo, useState } from 'react'
import axios from 'axios'
import { Dialog, Transition } from '@headlessui/react'
import type { TariffActivatePreview, TariffHistoryItem } from '../../../apis/types'
import {
    activateTariffHistoryApi,
    getTariffActivatePreviewApi,
    withdrawTariffHistoryApi,
} from '../../../apis/apis'
import { formatUTCDetailedDate } from '../../../util/string'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { TariffHistorySelect } from './TariffHistorySelect'
import { NewRatesetModal } from './NewRatesetModal'
import {
    getLatestHistoryCuid,
    isPendingRatesetStatus,
    sortHistoriesNewestFirst,
} from './tariffHistoryUtils'

const fieldLabelClass =
    'text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'

const actionButtonClass =
    'rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200'

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

type ActionKind = 'activate' | 'withdraw'

function apiErrorMessage(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message
        if (typeof message === 'string' && message.trim() !== '') {
            return message
        }
    }
    return fallback
}

export const TariffRatesetSection: React.FC<{
    histories: TariffHistoryItem[]
    selectedHistoryCuid: string | null | undefined
    tariffCuid: string
    canEdit: boolean
    onSelect: (historyCuid: string) => void
    onChanged: () => void
}> = ({
    histories,
    selectedHistoryCuid,
    tariffCuid,
    canEdit,
    onSelect,
    onChanged,
}) => {
    const dispatch = useAppDispatch()
    const orderedHistories = useMemo(() => sortHistoriesNewestFirst(histories), [histories])
    const latestCuid = getLatestHistoryCuid(orderedHistories)
    const selected =
        orderedHistories.find((h) => h.cuid === selectedHistoryCuid) ?? orderedHistories[0] ?? null
    const [newOpen, setNewOpen] = useState(false)
    const [action, setAction] = useState<ActionKind | null>(null)
    const [preview, setPreview] = useState<TariffActivatePreview | null>(null)
    const [reason, setReason] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (orderedHistories.length === 0) return null

    const vintageLabel =
        selected?.vintageYears != null && selected.vintageYears.length > 0
            ? selected.vintageYears.join(', ')
            : selected?.vintageYear ?? null
    const pending = selected != null && isPendingRatesetStatus(selected.status)

    const closeAction = (): void => {
        setAction(null)
        setPreview(null)
        setReason('')
        setError(null)
    }

    const startActivate = (): void => {
        if (selected == null) return
        setBusy(true)
        setError(null)
        void getTariffActivatePreviewApi(selected.cuid)
            .then((next) => {
                setPreview(next)
                setAction('activate')
            })
            .catch((err: unknown) => {
                void dispatch(
                    pushToast({
                        type: 'error',
                        message: apiErrorMessage(err, 'Failed to load activate preview'),
                    })
                )
            })
            .finally(() => setBusy(false))
    }

    const confirmAction = (): void => {
        if (selected == null || reason.trim() === '') return
        setBusy(true)
        setError(null)
        const body = { reason: reason.trim() }
        const request =
            action === 'activate'
                ? activateTariffHistoryApi(selected.cuid, body)
                : withdrawTariffHistoryApi(selected.cuid, body)
        void request
            .then(() => {
                closeAction()
                onChanged()
                void dispatch(pushToast({ type: 'success', message: 'Rateset updated' }))
            })
            .catch((err: unknown) => {
                setError(apiErrorMessage(err, 'Failed to update rateset'))
            })
            .finally(() => setBusy(false))
    }

    return (
        <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
                <span className={fieldLabelClass}>Rateset</span>
                {vintageLabel != null ? (
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                        PCIA year: {vintageLabel}
                    </span>
                ) : null}
                {canEdit ? (
                    <button type="button" className={actionButtonClass} onClick={() => setNewOpen(true)}>
                        New rateset
                    </button>
                ) : null}
                {canEdit && pending ? (
                    <>
                        <button
                            type="button"
                            className={actionButtonClass}
                            onClick={startActivate}
                            disabled={busy}
                        >
                            Activate
                        </button>
                        <button
                            type="button"
                            className={actionButtonClass}
                            onClick={() => setAction('withdraw')}
                            disabled={busy}
                        >
                            Withdraw
                        </button>
                    </>
                ) : null}
            </div>
            <TariffHistorySelect
                histories={orderedHistories}
                selectedHistoryCuid={selectedHistoryCuid}
                latestCuid={latestCuid}
                onSelect={onSelect}
            />
            {canEdit ? (
                <NewRatesetModal
                    open={newOpen}
                    tariffCuid={tariffCuid}
                    histories={orderedHistories}
                    onClose={() => setNewOpen(false)}
                    onCreated={(historyCuid) => {
                        onSelect(historyCuid)
                        onChanged()
                    }}
                />
            ) : null}
            <Transition show={action != null} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeAction}>
                    <div className="fixed inset-0 bg-black/50 dark:bg-black/70" />
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
                            <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-5 text-left shadow-xl dark:bg-slate-900">
                                <Dialog.Title className="text-lg font-medium text-slate-900 dark:text-slate-100">
                                    {action === 'activate'
                                        ? preview?.priorCurrent != null
                                            ? 'Activate this rateset and close the current one?'
                                            : 'Activate this rateset?'
                                        : 'Withdraw this rateset?'}
                                </Dialog.Title>
                                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                                    {action === 'activate' && preview != null ? (
                                        <>
                                            <p>
                                                Effective{' '}
                                                {formatUTCDetailedDate(new Date(preview.effectiveDate))}.{' '}
                                                {preview.rateCount.toLocaleString()} rate(s).
                                            </p>
                                            {preview.vintageYears.length > 0 ? (
                                                <p>PCIA year: {preview.vintageYears.join(', ')}</p>
                                            ) : null}
                                            {preview.priorCurrent != null ? (
                                                <p>
                                                    Closes the current rateset on{' '}
                                                    {formatUTCDetailedDate(
                                                        new Date(preview.priorCurrent.closesOn)
                                                    )}
                                                    .
                                                </p>
                                            ) : null}
                                            {preview.effectiveDateInPast ? (
                                                <p className="text-amber-800 dark:text-amber-200">
                                                    Effective date is in the past. Bills already processed
                                                    for that window used the prior rateset.
                                                </p>
                                            ) : null}
                                            {preview.orphanedRates.length > 0 ? (
                                                <div>
                                                    <p>
                                                        Charges on the prior rateset with no counterpart:
                                                    </p>
                                                    <ul className="mt-1 list-disc pl-5">
                                                        {preview.orphanedRates.map((rate) => (
                                                            <li
                                                                key={`${rate.name}-${rate.rateGroup}-${rate.season}-${rate.touPeriod}-${rate.vintageYear}`}
                                                            >
                                                                {rate.name}
                                                                {rate.vintageYear != null
                                                                    ? ` · PCIA ${rate.vintageYear}`
                                                                    : ''}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : null}
                                            <p>
                                                {preview.usageCount.toLocaleString()} active meter
                                                service(s).
                                            </p>
                                        </>
                                    ) : null}
                                    {action === 'withdraw' ? (
                                        <p>Withdrawn rates stay out of billing. This cannot be undone.</p>
                                    ) : null}
                                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                                        Reason <span className="font-normal text-slate-500">(required)</span>
                                        <textarea
                                            className={`${inputClass} mt-1`}
                                            rows={2}
                                            maxLength={500}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                    </label>
                                </div>
                                {error != null ? (
                                    <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
                                ) : null}
                                <div className="mt-5 flex justify-end gap-2">
                                    <button
                                        type="button"
                                        className={actionButtonClass}
                                        onClick={closeAction}
                                        disabled={busy}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
                                        onClick={confirmAction}
                                        disabled={busy || reason.trim() === ''}
                                    >
                                        {busy ? 'Saving…' : 'Confirm'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}

export default TariffRatesetSection
